import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ClientBase, FilterDataClients, Organization, OrganizationEdit, OrganizationTemplateCreate, OrgDayOff, OrgDayOffUpdate, Schedule, Vet } from '../../models';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from 'angular-2-local-storage';

@Injectable({
  providedIn: 'root'
})
export class OrganizationService {

  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService
  ) { }

  public getMyOrg() {
    return this.http.get<{
      data: Organization
    }>(`${environment.host}/organization/my_organization`);
  }

  public getOrg(id: number) {
    return this.http.get<{
      data: Organization
    }>(`${environment.host}/organization/${id}/`);
  }

  public getOrgSchedule() {
    return this.http.get<{
      data: {
        id: number,
        is_24_7: boolean;
        organization: number,
        schedule: Schedule
      }
    }>(`${environment.host}/organization/schedule`);
  }

  public getOrgDaysOff(id: number) {
    return this.http.get<{
      data: Array<OrgDayOff>
    }>(`${environment.host}/organization/${id}/day_off/list`);
  }

  public updateOrg(id: number, orgInfo: OrganizationEdit) {
    const formData = new FormData();
    formData.append('name', orgInfo.name);
    formData.append('information', orgInfo.information);
    if (orgInfo.logo) {
      formData.append('logo', orgInfo.logo);
    }

    return this.http.put<{
      data: Organization
    }>(`${environment.host}/organization/update/${id}/`, formData);
  }

  public updateScheduleOrg(schedule: Schedule) {
    return this.http.put<{
      data: string
    }>(`${environment.host}/organization/schedule`,{schedule});
  }

  public updateDayOffScheduleOrg(id: number, dayOff: OrgDayOff, shift: Array<Array<number>> | null) {
    return this.http.put<{
      data: string
    }>(`${environment.host}/organization/${id}/day_off/${dayOff.id}/set_shift`,{is_active: dayOff.is_active, shift});
  }

  public updateDaysOffScheduleOrg(id: number, day_offs: Array<OrgDayOffUpdate>) {
    return this.http.put<{
      data: string
    }>(`${environment.host}/organization/${id}/day_off/set`,{day_offs});
  }

  public removeOrgPhoto(id: number) {
    return this.http.delete<{
      data: string
    }>(`${environment.host}/organization/photo/${id}/`);
  }

  public addOrgPhoto(orgId: number, photo: File) {
    const formData = new FormData();
    formData.append('organization', orgId.toString());
    formData.append('photo', photo);

    return this.http.post<{
      data: string
    }>(`${environment.host}/organization/photo/`, formData);
  }

  public getVets(is_surgeon?: boolean, service_type?: number) {
    const isGrooming = this.localStorageService.get('is-grooming');
    let queries = '?role=' + (isGrooming ? 'org_admin' : 'doctor');
    if (is_surgeon) {
      queries = queries + '&is_surgeon=' + is_surgeon;
    }

    if (service_type) {
      queries = queries + '&service_type=' + service_type;
    }

    return this.http.get<{
      code: string;
      data: Array<Vet>;
      details: string;
      message: string;
    }>(`${environment.host}/organization/employee/${queries}`);
  }

  public getClients(data: FilterDataClients) {
    let queries = '?page=' + (data.page || 1) + '&page_size=' + (data.page_size || 10);
    
    if (data.order_by_pet_type_id) {
      queries = '?order_by_pet_type_id=' + data.order_by_pet_type_id;
    }

    if (data.client) {
      queries = queries + '&client=' + data.client;
    }

    if (data.pet_types) {
      queries = queries + '&pet_types=' + data.pet_types;
    }

    if (data.specialists) {
      queries = queries + '&specialists=' + data.specialists;
    }

    if (data.search) {
      queries = queries + '&search=' + data.search;
    }

    return this.http.get<{
      code: string;
      data: {
        count: number,
        results: Array<ClientBase>
      };
      details: string;
      message: string;
    }>(`${environment.host}/organization/clients/${queries}`);
  }

  public addTemplate(data: OrganizationTemplateCreate) {
    return this.http.post<{
      data: OrganizationTemplateCreate
    }>(`${environment.host}/organization/template/`, data);
  }

  public addOnlineTemplate(data: OrganizationTemplateCreate) {
    return this.http.post<{
      data: OrganizationTemplateCreate
    }>(`${environment.host}/organization/online/template/`, data);
  }
}
